import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "mt-1 w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-signal transition-colors duration-300";
const labelCls = "text-[10px] tracking-[0.2em] uppercase text-zinc-500";
const sectionCls = "text-[11px] tracking-[0.3em] uppercase text-signal mb-4";

const TextField = ({ label, value, onChange, testid, textarea }) => (
    <label className="block">
        <span className={labelCls}>{label}</span>
        {textarea ? (
            <textarea rows={3} className={`${inputCls} resize-y`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        ) : (
            <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        )}
    </label>
);

export const ContentTab = ({ token }) => {
    const [content, setContent] = useState(null);
    const [msg, setMsg] = useState("");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        axios.get(`${API}/site`).then((r) => setContent(r.data.content)).catch(() => {});
    }, []);

    if (!content) {
        return <p className="text-zinc-500 text-sm" data-testid="admin-content-loading">Loading content…</p>;
    }

    const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));
    const setChapter = (i, k, v) =>
        setContent((c) => ({ ...c, chapters: c.chapters.map((ch, j) => (j === i ? { ...ch, [k]: v } : ch)) }));
    const setShot = (i, k, v) =>
        setContent((c) => ({ ...c, lookbook_shots: c.lookbook_shots.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

    const save = async () => {
        try {
            const { key, ...rest } = content;
            await axios.put(`${API}/admin/content`, rest, auth);
            setMsg("Saved — live on the site");
            setTimeout(() => setMsg(""), 2500);
        } catch {
            setMsg("Save failed");
        }
    };

    return (
        <div className="space-y-12" data-testid="admin-content-tab">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-semibold uppercase tracking-tight text-2xl">Page Content</h2>
                <button onClick={save} data-testid="admin-content-save" className="bg-white text-void px-6 py-2 text-[10px] tracking-[0.25em] uppercase hover:bg-signal hover:text-white transition-colors duration-300">
                    Save all
                </button>
            </div>
            {msg && (
                <p className="text-signal text-xs tracking-[0.2em] uppercase" data-testid="admin-content-msg">
                    {msg}
                </p>
            )}

            <section>
                <h3 className={sectionCls}>General</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <TextField label="Nav season label" value={content.nav_season || ""} onChange={(v) => set("nav_season", v)} testid="admin-content-nav-season" />
                    <TextField label="Collection section title" value={content.collection_title || ""} onChange={(v) => set("collection_title", v)} testid="admin-content-collection-title" />
                </div>
            </section>

            <section>
                <h3 className={sectionCls}>Hero</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <TextField label="Overline" value={content.hero_overline} onChange={(v) => set("hero_overline", v)} testid="admin-content-hero-overline" />
                    <TextField label="Coordinates" value={content.hero_coords} onChange={(v) => set("hero_coords", v)} testid="admin-content-hero-coords" />
                    <div className="md:col-span-2">
                        <TextField label="Tagline" value={content.hero_tagline} onChange={(v) => set("hero_tagline", v)} testid="admin-content-hero-tagline" textarea />
                    </div>
                </div>
            </section>

            <section>
                <h3 className={sectionCls}>Editorial Marquee</h3>
                <TextField
                    label="Phrases (one per line)"
                    value={(content.marquee || []).join("\n")}
                    onChange={(v) => set("marquee", v.split("\n").filter((s) => s.trim()))}
                    testid="admin-content-marquee"
                    textarea
                />
            </section>

            <section>
                <h3 className={sectionCls}>Manifesto Chapters</h3>
                <div className="space-y-6">
                    {(content.chapters || []).map((ch, i) => (
                        <div key={ch.n} className="border border-zinc-800 p-4 grid gap-3" data-testid={`admin-chapter-${i}`}>
                            <TextField label={`Chapter ${ch.n} title`} value={ch.title} onChange={(v) => setChapter(i, "title", v)} testid={`admin-chapter-title-${i}`} />
                            <TextField label="Body" value={ch.body} onChange={(v) => setChapter(i, "body", v)} testid={`admin-chapter-body-${i}`} textarea />
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <h3 className={sectionCls}>Lookbook</h3>
                <div className="grid gap-4">
                    <TextField label="Lede" value={content.lookbook_lede || ""} onChange={(v) => set("lookbook_lede", v)} testid="admin-content-lookbook-lede" />
                    {(content.lookbook_shots || []).map((s, i) => (
                        <div key={i} className="border border-zinc-800 p-4 grid gap-3" data-testid={`admin-shot-${i}`}>
                            <TextField label={`Look ${i + 1} image URL`} value={s.url} onChange={(v) => setShot(i, "url", v)} testid={`admin-shot-url-${i}`} />
                            <TextField label="Caption" value={s.caption} onChange={(v) => setShot(i, "caption", v)} testid={`admin-shot-caption-${i}`} />
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <h3 className={sectionCls}>Collection & Drop</h3>
                <div className="grid gap-4">
                    <TextField label="Collection blurb" value={content.collection_blurb} onChange={(v) => set("collection_blurb", v)} testid="admin-content-collection-blurb" textarea />
                    <TextField label="Drop title" value={content.drop_title} onChange={(v) => set("drop_title", v)} testid="admin-content-drop-title" />
                    <TextField label="Drop line" value={content.drop_line} onChange={(v) => set("drop_line", v)} testid="admin-content-drop-line" />
                    <TextField label="Drop date — countdown target (YYYY-MM-DD)" value={content.drop_date || ""} onChange={(v) => set("drop_date", v)} testid="admin-content-drop-date" />
                    <TextField label="Footer contact email" value={content.footer_email} onChange={(v) => set("footer_email", v)} testid="admin-content-footer-email" />
                </div>
            </section>

            <section>
                <h3 className={sectionCls}>Footer</h3>
                <div className="grid gap-4">
                    <TextField label="Social links (one per line)" value={(content.footer_socials || []).join("\n")} onChange={(v) => set("footer_socials", v.split("\n").filter((s) => s.trim()))} testid="admin-content-footer-socials" textarea />
                    <TextField label="Legal links (one per line)" value={(content.footer_legal || []).join("\n")} onChange={(v) => set("footer_legal", v.split("\n").filter((s) => s.trim()))} testid="admin-content-footer-legal" textarea />
                    <TextField label="Footer tagline" value={content.footer_tagline || ""} onChange={(v) => set("footer_tagline", v)} testid="admin-content-footer-tagline" />
                    <TextField label="Copyright line" value={content.footer_copyright || ""} onChange={(v) => set("footer_copyright", v)} testid="admin-content-footer-copyright" />
                </div>
            </section>
        </div>
    );
};
